/* eslint-disable complexity */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable no-restricted-imports */
/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { NotAvailable } from '@/components/blocks/NotAvailable';
import { ProductDetailsAddToCart } from '@/components/blocks/ProductDetails/AddToCart';
import { ProductDetailsDisplay } from '@/components/blocks/ProductDetails/Display';
import { ProductDetailsPromotionShort } from '@/components/blocks/ProductDetails/Aplazo';
import { ProductDetailsGallery } from '@/components/blocks/ProductDetails/Gallery';
import {
	ProductDetailsPrice,
	useProductDetailsPrice,
} from '@/components/blocks/ProductDetails/Price';
import { ProductDetailsPromos } from '@/components/blocks/ProductDetails/Promos';
import { ProductDetailsSeller } from '@/components/blocks/ProductDetails/Seller';
import {
	productDetailsBinaryElementSX,
	productDetailsImageBinaryElementSX,
	productDetailsImageBinaryElementMobileSX,
	productDetailsImageBinaryElementMobile2SX,
	productDetailsPromosElementMobile2SX,
} from '@/components/blocks/ProductDetails/styles/binaryElement';
import { productDetailsContainerSX } from '@/components/blocks/ProductDetails/styles/container';
import { ProductDetailsAttributes } from '@/components/content/ProductDetails/parts/Attributes';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useLocalization } from '@/data/Localization';
import { ContentProvider } from '@/data/context/content';
import { useStoreLocatorState } from '@/data/state/useStoreLocatorState';
import { ID } from '@/data/types/Basic';
import { Paper, Stack, Divider, useMediaQuery, Box, Portal } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { CheckAvailabilityDisplay } from '@/components/blocks/ProductDetails/CheckAvailability';
import { ProductDetailsBenefitsDisplay } from '@/components/blocks/ProductDetails/Benefits';
import { ProductDescriptionDetails } from '@/components/blocks/ProductDetails/ProductDescriptionDetails';
import { ColorProduct } from '@/components/blocks/ProductDetails/ColorProduct';
import { CompleteLook } from '@/components/content/ProductDetails/parts/CompleteLook';
import {
	SpotBackToSchool,
	SpotBottomRight,
	SpotCarousels,
} from '@/components/content/ProductDetails/parts/SpotBottomRight';
import { ProductDetailsBenefitsMobileDisplay } from '@/components/blocks/ProductDetails/BenefitsMobile';
import { ContentRecommendation } from '@/components/content/ContentRecommendation';
import { useIsDpElectronico } from '@/data/Content/DpElectronicos';
import { ProductDetailsDrawer } from './parts/ProductDetailsDrawer';
import { ProductDetailsAvailability } from '@/components/blocks/ProductDetails/Availability';
import { usePriceBy } from '@/data/Content/Prices';
import { useCart } from '@/data/Content/Cart';
import { ProductDetailsSkeleton } from './parts/ProductDetailsSkeleton';
import { FreeShipping } from './parts/FreeShiping';
import { useStock } from '@/components/blocks/ProductDetails/hooks/useStock';
import { ProductCustomPlayer } from '@/components/content/ProductDetails/parts/ProductCustomPlayer';
import { PDPExactPromotionContentRecommendation } from '@/components/content/ProductDetails/parts/ProductSpot';

export interface InfoElectronico {
	hasOfferDeContado: boolean;
	prices: {
		offerDpVale: undefined;
		offerDeContado: string | undefined;
		strikedDeContado: string | undefined;
	};
}

export const ProductDetails: FC<{
	id: ID;
}> = ({ id }) => {
	const {
		dimensions: { contentSpacing },
	} = useTheme();
	const { data: cartDetails } = useCart();
	const { storeLocator } = useStoreLocatorState();
	const [physicalStoreName, setPhysicalStoreName] = useState<string>('');
	const productDetails = useProductDetails({ partNumber: id.toString(), physicalStoreName });
	const { product, selection, availability, onSelectionInitialElectronic } = productDetails;
	const { detailsNotAvailable } = useLocalization('productDetail');
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

	const { isDpElectronico, isLoading: isDpElectronicoLoading } = useIsDpElectronico(product?.id);

	const { data: dpElectronicosPrices, isLoading: isDpElectronicoPricesLoading } = usePriceBy(
		isDpElectronico ? { catalogEntryId: product?.id } : undefined
	);

	const infoElectronico = useMemo<InfoElectronico | undefined>(() => {
		if (!isDpElectronico || !dpElectronicosPrices || !product) {
			return undefined;
		}

		return {
			hasOfferDeContado: product['x_prices.field1'] ? true : false,
			prices: {
				offerDpVale: undefined,
				offerDeContado: dpElectronicosPrices.EntitledPrice?.at(0)
					?.UnitPrice?.at(0)
					?.price?.value?.toString(),
				strikedDeContado: product['x_prices.field1'],
			},
		};
	}, [product, dpElectronicosPrices, isDpElectronico]);

	const [shouldShowSlide, setShouldShowSlide] = useState(false);

	const refButton = useRef<HTMLDivElement>(null);
	const refEnd = useRef<HTMLDivElement>(null);

	useEffect(() => {
		setPhysicalStoreName(storeLocator.selectedStore?.physicalStoreName ?? '');
	}, [storeLocator.selectedStore]);

	useEffect(() => {
		if (infoElectronico) {
			onSelectionInitialElectronic();
		}
	}, [infoElectronico, onSelectionInitialElectronic]);

	useEffect(() => {
		if (!isMobile) return;

		let ticking = false;

		const updatePosition = () => {
			if (!refButton.current || !refEnd.current) return;

			const buttonRect = refButton.current.getBoundingClientRect();
			const endRect = refEnd.current.getBoundingClientRect();

			const isPastButton = buttonRect.bottom <= 70;
			const isBeforeEnd =
				endRect.top >= (globalThis.document?.documentElement?.clientHeight || 800) - 150;

			setShouldShowSlide(isPastButton && isBeforeEnd);
		};

		updatePosition();

		const onScroll = () => {
			if (!ticking) {
				globalThis.requestAnimationFrame(() => {
					updatePosition();
					ticking = false;
				});
				ticking = true;
			}
		};

		globalThis.addEventListener('scroll', onScroll, { passive: true });
		globalThis.addEventListener('resize', onScroll, { passive: true });

		return () => {
			globalThis.removeEventListener('scroll', onScroll);
			globalThis.removeEventListener('resize', onScroll);
		};
	}, [isMobile]);

	const { someWstock } = useStock(availability);

	const sku = selection?.sku || product;

	const productDetailsPrice = useProductDetailsPrice(product, sku);

	if (productDetails.loading || isDpElectronicoLoading || isDpElectronicoPricesLoading) {
		return <ProductDetailsSkeleton isMobile={isMobile} />;
	}

	if (!selection || !product) {
		return <NotAvailable message={detailsNotAvailable.t()} />;
	}

	return (
		<>
			<ContentProvider value={productDetails}>
				<Paper sx={productDetailsContainerSX({ isMobile })}>
					<Stack spacing={contentSpacing}>
						<Stack
							direction={{
								md: 'row',
							}}
							spacing={1}
							justifyContent="space-between"
						>
							{isMobile ? (
								<React.Fragment>
									<Box>
										<Stack spacing={1} sx={productDetailsImageBinaryElementMobileSX}>
											<ProductDetailsDisplay />
											<ProductDetailsPrice
												infoElectronico={infoElectronico}
												orderId={cartDetails?.orderId}
											/>
										</Stack>
										<Stack spacing={1} sx={productDetailsImageBinaryElementSX}>
											<ProductDetailsGallery />
											<FreeShipping />
										</Stack>
										<Stack spacing={1} sx={productDetailsPromosElementMobile2SX}>
											<ProductDetailsPromos />
										</Stack>
									</Box>
									<Stack spacing={1} sx={productDetailsImageBinaryElementMobile2SX}>
										<Box ref={refButton}>
											<ProductDetailsPromotionShort />
											<PDPExactPromotionContentRecommendation partNumber={id.toString()} />
											<Divider variant="middle" sx={{ my: '8px' }} />
											<Stack sx={{ marginBottom: '10px' }}>
												<ProductDetailsAvailability />
											</Stack>
											<ColorProduct />
											<ProductCustomPlayer />
											{!isDpElectronico && productDetails.isBuyable ? (
												<ProductDetailsAttributes />
											) : null}
											<ProductDetailsSeller />
											{productDetails.isBuyable ? <ProductDetailsAddToCart /> : null}
										</Box>
										{someWstock ? (
											<>
												{!isDpElectronico && productDetails.isBuyable ? (
													<Portal>
														<Box
															sx={{
																width: '100%',
																position: 'fixed',
																bottom: 0,
																left: 0,
																right: 0,
																zIndex: 1001,
																backgroundColor: 'white',
																padding: '10px',
																paddingBottom: 'calc(10px + env(safe-area-inset-bottom, 0px))',
																transform: shouldShowSlide ? 'translateY(0)' : 'translateY(100%)',
																boxShadow: shouldShowSlide
																	? '0px -4px 12px rgba(0,0,0,0.08)'
																	: 'none',
																transition:
																	'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
																pointerEvents: shouldShowSlide ? 'auto' : 'none',
															}}
														>
															<ProductDetailsDrawer />
														</Box>
													</Portal>
												) : null}
											</>
										) : null}

										<CheckAvailabilityDisplay isDpElectronico={isDpElectronico} />
										<Divider variant="middle" />
										<ProductDetailsBenefitsMobileDisplay />
										<SpotBottomRight />

										<CompleteLook id={id} />

										<ProductDescriptionDetails isDpElectronico={isDpElectronico} />
									</Stack>
								</React.Fragment>
							) : (
								<>
									<Stack spacing={1} sx={productDetailsImageBinaryElementSX}>
										<ProductDetailsGallery />
										<ProductDescriptionDetails isDpElectronico={isDpElectronico} />
									</Stack>
									<Stack spacing={0} sx={productDetailsBinaryElementSX}>
										<ProductDetailsDisplay isElectronic={isDpElectronico} />
										{product.type === 'bundle' ? null : (
											<Stack spacing={0}>
												<ProductDetailsPrice
													infoElectronico={infoElectronico}
													orderId={cartDetails?.orderId}
												/>
												<ProductDetailsPromotionShort />
												<PDPExactPromotionContentRecommendation partNumber={id.toString()} />
												<ProductDetailsPromos />
												<Divider sx={{ my: '10px' }} />
												<Stack sx={{ marginBottom: '10px' }}>
													<ProductDetailsAvailability />
												</Stack>
												<ColorProduct />
												<ProductCustomPlayer />
												{!isDpElectronico && productDetails.isBuyable ? (
													<ProductDetailsAttributes />
												) : null}
												<ProductDetailsSeller />
												{productDetails.isBuyable ? <ProductDetailsAddToCart /> : null}
												<CheckAvailabilityDisplay isDpElectronico={isDpElectronico} />
												<Divider sx={{ my: '16px' }} />
												<ProductDetailsBenefitsDisplay />
												<SpotBottomRight />
												<ContentRecommendation
													id={'DPV2_WhatsAppFloating_Spot'}
													properties={{
														emsName: 'DPV2_WhatsAppFloating_Spot',
													}}
												/>
											</Stack>
										)}
									</Stack>
								</>
							)}
						</Stack>
					</Stack>
				</Paper>
				<Box ref={refEnd}></Box>
				{!isMobile ? <CompleteLook id={id} /> : null}
				<SpotCarousels />
				<SpotBackToSchool />
			</ContentProvider>
		</>
	);
};
